/**
 * 产品一览表 sync card — admin-only. Manually triggers pulling the FM
 * ``product_photo`` layout into the ``product_overview`` collection.
 * Mirrors the layout of ``ProcurementSyncCard`` so admins get a consistent UX.
 *
 * 2026-02-27 v2 · Added a Stop button — cooperatively cancels the in-flight
 * FM sync via POST /sync/stop.  The worker loop checks ``stop_requested``
 * at the top of every page so the counters stay accurate.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, RefreshCw, StopCircle, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

export function ProductOverviewSyncCard() {
  const { t } = useLanguage();
  const [status, setStatus] = useState({ state: "idle", total: 0, processed: 0, upserted: 0, photos_downloaded: 0, error: null });
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // 2026-06 · 后台空闲自动 AI（生成描述 / 抠图）
  const [autoAi, setAutoAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const fetchAutoAi = async () => {
    try {
      const { data } = await api.get("/product-overview/auto-ai");
      setAutoAi(data);
    } catch (_) { /* non-admin or transient */ }
  };

  const toggleAuto = async (key) => {
    try {
      const { data } = await api.post("/product-overview/auto-ai", { [key]: !autoAi?.[key] });
      setAutoAi((prev) => ({ ...prev, ...data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runAutoNow = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/product-overview/auto-ai/run");
      toast.success(data.started
        ? (t("po.autoAiStarted") || "已开始处理一批，稍后自动刷新")
        : (t("po.autoAiBusy") || "正在处理中，请稍候"));
      setTimeout(fetchAutoAi, 5000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/product-overview/sync/status");
      setStatus(data);
    } catch (_) { /* ignore transient failures */ }
  };

  useEffect(() => {
    fetchAutoAi();
    const iv = setInterval(fetchAutoAi, autoAi?.running ? 5000 : 30000);
    return () => clearInterval(iv);
  }, [autoAi?.running]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, status.state === "running" ? 2000 : 5000);
    return () => clearInterval(iv);
  }, [status.state]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/product-overview/sync/start");
      toast.success(t("po.syncStarted") || "同步已启动");
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("po.syncFailed"));
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    setStopping(true);
    try {
      await api.post("/product-overview/sync/stop");
      toast.success(t("po.syncStopRequested") || "已请求停止，等待当前批次结束…");
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("po.syncStopFailed") || "停止失败");
    } finally {
      setStopping(false);
    }
  };

  const running = status.state === "running";
  const wasStopped = status.state === "stopped";
  const pct = status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

  return (
    <div className="bg-card border border-border rounded-md p-4 space-y-3" data-testid="po-sync-card">
      <div className="flex items-center gap-2">
        <ImageIcon size={16} className="text-brand" />
        <h3 className="text-sm font-semibold">{t("po.syncTitle") || "产品一览表同步 (FileMaker)"}</h3>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {t("po.syncHint") || "从 FileMaker 「产品一览表 · product_photo」拉取产品图片和元数据；图片会自动下载并托管到 R2，扫码/浏览无需 FM 凭证。"}
      </p>
      {running && (
        <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
          <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-xs font-mono text-muted-foreground">
        {running
          ? `${t("po.syncing") || "同步中"} ${status.processed}/${status.total} (${pct}%) · ${status.photos_downloaded} ${t("po.photos") || "张图片"}`
          : wasStopped
            ? `${t("po.syncStopped") || "已停止"}: ${status.processed}/${status.total || "?"} · ${status.photos_downloaded} ${t("po.photos") || "张图片"}`
            : status.state === "done"
              ? (t("po.syncDone") || "同步完成：已更新 {n} 条记录，下载 {p} 张图片")
                  .replace("{n}", status.upserted).replace("{p}", status.photos_downloaded)
              : status.state === "error"
                ? `${t("po.syncFailed") || "同步失败"}: ${status.error || ""}`
                : t("po.syncIdle") || "尚未同步"}
      </p>
      {/* 2026-06 · 每天 07:30 (美东) 自动同步（只拉文字，图片由后台补） */}
      <p className="text-[11px] font-mono text-muted-foreground" data-testid="po-auto-sync-info">
        {t("po.autoSyncDaily").replace("{time}", status.auto_daily_at || "07:30")}
        {status.last_auto
          ? ` · ${t("po.autoSyncLast")
              .replace("{at}", new Date(status.last_auto.at).toLocaleString())
              .replace("{n}", status.last_auto.upserted ?? 0)}`
          : ` · ${t("po.autoSyncNever")}`}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={start} disabled={running || starting} data-testid="po-sync-start">
          {running || starting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {running ? (t("po.syncing") || "同步中") : (t("po.sync") || "开始同步")}
        </Button>
        {running && (
          <Button size="sm" variant="destructive" onClick={stop} disabled={stopping || status.stop_requested}
                  data-testid="po-sync-stop">
            {stopping ? <Loader2 size={14} className="animate-spin" /> : <StopCircle size={14} />}
            {status.stop_requested ? (t("po.syncStopping") || "正在停止…") : (t("po.syncStop") || "停止同步")}
          </Button>
        )}
      </div>

      {/* 后台空闲自动 AI */}
      {autoAi && (
        <div className="pt-3 border-t border-border/60 space-y-2" data-testid="po-auto-ai-panel">
          <p className="text-xs font-semibold">{t("po.autoAiTitle") || "后台空闲自动处理"}</p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {t("po.autoAiHint") || "每 10 分钟自动处理一小批；同步进行中或磁盘不足时自动跳过。"}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={autoAi.descriptions ? "default" : "outline"}
              onClick={() => toggleAuto("descriptions")} data-testid="po-auto-ai-desc-toggle">
              {t("po.autoAiDesc") || "自动生成描述"} · {autoAi.descriptions ? "ON" : "OFF"}
            </Button>
            <Button size="sm" variant="outline" onClick={runAutoNow} disabled={aiBusy}
              data-testid="po-auto-ai-run-now">
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {t("po.autoAiRunNow") || "立即处理一批"}
            </Button>
          </div>
          {/* 实时进度 */}
          {autoAi.running && autoAi.progress?.total > 0 && (
            <div className="space-y-1" data-testid="po-auto-ai-progress">
              <div className="flex items-center justify-between text-[11px] font-mono">
                <span>{(t("po.autoAiProgress") || "正在处理 {kind} {done}/{total}")
                  .replace("{kind}", autoAi.progress.kind === "cutouts" ? (t("po.autoAiCutout") || "抠图") : (t("po.autoAiDesc") || "描述"))
                  .replace("{done}", autoAi.progress.done).replace("{total}", autoAi.progress.total)}</span>
                <span>{Math.round((autoAi.progress.done / autoAi.progress.total) * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-brand transition-all duration-500"
                  style={{ width: `${Math.min(100, (autoAi.progress.done / autoAi.progress.total) * 100)}%` }} />
              </div>
            </div>
          )}
          {/* 总体剩余进度（已完成 / 总数） */}
          {(() => {
            const doneDesc = Math.max(0, (autoAi.total_records || 0) - (autoAi.pending_descriptions || 0));
            const pct = autoAi.total_records ? Math.round((doneDesc / autoAi.total_records) * 100) : 0;
            return (
              <div className="space-y-1" data-testid="po-auto-ai-overall">
                <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                  <span>{t("po.autoAiDesc") || "自动生成描述"}: {doneDesc}/{autoAi.total_records || 0}</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })()}
          {autoAi.last_run && (
            <p className="text-[11px] text-muted-foreground" data-testid="po-auto-ai-last">
              {(t("po.autoAiLast") || "上次运行 {time} · 新增 {d} 条描述 · {c} 张抠图{skip}")
                .replace("{time}", new Date(autoAi.last_run).toLocaleString())
                .replace("{d}", autoAi.last_result?.descriptions ?? 0)
                .replace("{c}", autoAi.last_result?.cutouts ?? 0)
                .replace("{skip}", autoAi.last_result?.skipped ? ` (${autoAi.last_result.skipped})` : "")}
            </p>
          )}
          <p className="text-[11px] font-mono text-muted-foreground" data-testid="po-auto-ai-pending">
            {(t("po.autoAiPending") || "待处理：{d} 条缺描述 · {c} 张待抠图（App 上传） · 每批 {b} 条")
              .replace("{d}", autoAi.pending_descriptions)
              .replace("{c}", autoAi.pending_cutouts ?? 0)
              .replace("{b}", autoAi.batch_size)}
          </p>
        </div>
      )}
    </div>
  );
}
